import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Switch } from '../../lib/shadcn/switch'
import { useGetCustomer, useUpdateCustomer } from '../../hooks/backend/customersPage'
import { toast } from '../../lib/shadcn/sonner'
import { reportStoreLocatorSync, type StoreLocatorSyncResult } from './storeLocatorSync'
import { HelpText } from '../../components/HelpText'

export type DeliveryMethod = 'shipping' | 'delivery' | 'free_delivery'

export interface CustomerFormFields {
  contact_name: string
  company_name: string
  contact_number: string
  first_name: string
  last_name: string
  email: string
  billing_address: string
  billing_city: string
  billing_state: string
  billing_zip: string
  billing_phone: string
  shipping_address: string
  shipping_city: string
  shipping_state: string
  shipping_zip: string
  shipping_phone: string
  website: string
  delivery_method: DeliveryMethod
  store_locator: boolean
  active: boolean
  reorder_reminders: boolean
  retail: boolean
}

const EMPTY_FORM: CustomerFormFields = {
  contact_name: '',
  company_name: '',
  contact_number: '',
  first_name: '',
  last_name: '',
  email: '',
  billing_address: '',
  billing_city: '',
  billing_state: '',
  billing_zip: '',
  billing_phone: '',
  shipping_address: '',
  shipping_city: '',
  shipping_state: '',
  shipping_zip: '',
  shipping_phone: '',
  website: '',
  delivery_method: 'shipping',
  store_locator: false,
  active: true,
  reorder_reminders: true,
  retail: false,
}

interface Props {
  customerId: string | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

export default function CustomerEditDialog({ customerId, onOpenChange, onSaved }: Props) {
  const getCustomer = useGetCustomer()
  const updateCustomer = useUpdateCustomer()
  const [form, setForm] = useState<CustomerFormFields>(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [storeLocatorError, setStoreLocatorError] = useState<string | null>(null)

  useEffect(() => {
    if (!customerId) return
    setLoading(true)
    setForm(EMPTY_FORM)
    setStoreLocatorError(null)
    getCustomer
      .trigger({ customer_id: customerId }, { skipCache: true })
      .then((data: unknown) => {
        const row = (data ?? {}) as Partial<CustomerFormFields> & { store_locator_error?: string | null }
        setForm({
          contact_name: row.contact_name ?? '',
          company_name: row.company_name ?? '',
          contact_number: row.contact_number ?? '',
          first_name: row.first_name ?? '',
          last_name: row.last_name ?? '',
          email: row.email ?? '',
          billing_address: row.billing_address ?? '',
          billing_city: row.billing_city ?? '',
          billing_state: row.billing_state ?? '',
          billing_zip: row.billing_zip ?? '',
          billing_phone: row.billing_phone ?? '',
          shipping_address: row.shipping_address ?? '',
          shipping_city: row.shipping_city ?? '',
          shipping_state: row.shipping_state ?? '',
          shipping_zip: row.shipping_zip ?? '',
          shipping_phone: row.shipping_phone ?? '',
          website: row.website ?? '',
          store_locator: row.store_locator === true,
          active: row.active !== false,
          reorder_reminders: row.reorder_reminders !== false,
          retail: row.retail === true,
          delivery_method:
            row.delivery_method === 'delivery' || row.delivery_method === 'free_delivery'
              ? row.delivery_method
              : 'shipping',
        })
        setStoreLocatorError(row.store_locator_error ?? null)
      })
      .catch(() => toast.error('Failed to load customer'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  type TextField = Exclude<keyof CustomerFormFields, 'delivery_method' | 'store_locator' | 'active' | 'reorder_reminders' | 'retail'>

  const field = (key: TextField, label: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={`customer-${key}`}>{label}</Label>
      <Input
        id={`customer-${key}`}
        value={form[key]}
        disabled={loading}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
      />
    </div>
  )

  const handleSave = async () => {
    if (!customerId) return
    setSaving(true)
    try {
      const result = (await updateCustomer.trigger({ customer_id: customerId, ...form })) as {
        store_locator_sync?: StoreLocatorSyncResult
      } | null
      toast.success('Customer updated')
      reportStoreLocatorSync(result?.store_locator_sync)
      onSaved()
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update customer')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={customerId !== null}
      onOpenChange={open => {
        if (!open && !saving) onOpenChange(false)
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Customer</DialogTitle>
          <DialogDescription>Update contact and address details for this customer.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field('company_name', 'Company Name')}
              {field('contact_name', 'Contact Name')}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="customer-delivery-method">Delivery Method</Label>
              <Select
                value={form.delivery_method}
                onValueChange={value => setForm(prev => ({ ...prev, delivery_method: value as DeliveryMethod }))}
              >
                <SelectTrigger id="customer-delivery-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shipping">Shipping</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="free_delivery">Free Delivery</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Orders for shipping customers show a Ship action on the Orders page; delivery and free delivery customers do not.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {field('first_name', 'First Name')}
              {field('last_name', 'Last Name')}
              {field('contact_number', 'Customer #')}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field('email', 'Email')}
              {field('billing_phone', 'Billing Phone')}
            </div>
            {field('website', 'Website')}

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="customer-active">Active</Label>
                <p className="text-xs text-muted-foreground">
                  Inactive customers are hidden on the Customers page unless you show them, and never get reorder reminders.
                </p>
              </div>
              <Switch
                id="customer-active"
                checked={form.active}
                disabled={loading}
                onCheckedChange={checked => setForm(prev => ({ ...prev, active: checked }))}
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="customer-retail">Retail customer</Label>
                <p className="text-xs text-muted-foreground">
                  Retail customers are hidden on the Customers page unless you show them, never get reorder
                  reminders, and are left out of the weekly digest, tier review and B2B sync.
                </p>
              </div>
              <Switch
                id="customer-retail"
                checked={form.retail}
                disabled={loading}
                onCheckedChange={checked => setForm(prev => ({ ...prev, retail: checked }))}
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="customer-reorder-reminders">Include in reorder reminders</Label>
                <p className="text-xs text-muted-foreground">
                  Lists this customer under CRM, Reorder reminders when they're past their usual ordering pace.
                </p>
              </div>
              <Switch
                id="customer-reorder-reminders"
                checked={form.reorder_reminders && form.active && !form.retail}
                disabled={loading || !form.active || form.retail}
                onCheckedChange={checked => setForm(prev => ({ ...prev, reorder_reminders: checked }))}
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="customer-store-locator">Show on Web store locator</Label>
                <p className="text-xs text-muted-foreground"><HelpText>
                  Lists this customer on the store locator at poppinwtx.com (not B2B) with their shipping
                  address (or billing if there's none) and website. Phone and email aren't shared. Saving keeps it up to date.
                </HelpText></p>
                {form.store_locator && storeLocatorError ? (
                  <p className="text-xs text-destructive">{storeLocatorError}</p>
                ) : null}
              </div>
              <Switch
                id="customer-store-locator"
                checked={form.store_locator}
                disabled={loading}
                onCheckedChange={checked => setForm(prev => ({ ...prev, store_locator: checked }))}
              />
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-medium text-foreground">Billing Address</h3>
              {field('billing_address', 'Street Address')}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {field('billing_city', 'City')}
                {field('billing_state', 'State')}
                {field('billing_zip', 'Zip')}
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-medium text-foreground">Shipping Address</h3>
              {field('shipping_address', 'Street Address')}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {field('shipping_city', 'City')}
                {field('shipping_state', 'State')}
                {field('shipping_zip', 'Zip')}
              </div>
              {field('shipping_phone', 'Shipping Phone')}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving || loading}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Saving…
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
